"use client";

import React from "react";
import { MapPin, Navigation, Phone, Map, Globe, Building2, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hubs = [{
  id: "hub-1",
  name: "Albany Main Garage",
  location: "Albany, NY",
  focus: "Full Service & Engine Repair",
  status: "Active"
}, {
  id: "hub-2",
  name: "Schenectady Service Point",
  location: "Schenectady, NY",
  focus: "Brakes & Suspension Specialists",
  status: "EXPANDING"
}, {
  id: "hub-3",
  name: "Troy Restoration Center",
  location: "Troy, NY",
  focus: "Classic Car Care & Body Work",
  status: "Expanding"
}];
export default function OurHeadquarters() {
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll();
  return <section data-section-id="224943" id="visit-the-shop" className="dark bg-background w-full overflow-hidden">
      {}
      <div className="relative w-full h-[600px] lg:h-[700px] bg-secondary border-y border-border">
        {}
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660796/img/racing_green_map_texture.png')] bg-cover bg-center opacity-90" aria-hidden="true" />
        
        {}
        <div className="absolute inset-0 bg-primary/20 mix-blend-multiply" aria-hidden="true" />

        {}
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660796/img/vintage_garage_grid.png')] bg-repeat opacity-5" style={{
        backgroundSize: '40px 40px'
      }} aria-hidden="true" />

        {}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full flex flex-col items-center z-10">
          <div className="relative flex items-center justify-center">
            <div className="absolute size-8 bg-primary/30 rounded-full animate-ping" />
            <div className="absolute size-4 bg-primary/60 rounded-full animate-pulse" />
            <MapPin className="size-12 text-primary relative z-10 drop-shadow-lg" fill="currentColor" />
          </div>
          <div className="mt-2 px-3 py-1 bg-background/90 backdrop-blur-sm border border-border text-xs font-mono font-medium text-foreground uppercase tracking-wider shadow-sm">EST. 1921</div>
        </div>

        {}
        <div className="absolute top-6 left-4 right-4 md:top-12 md:left-12 md:right-auto z-20 md:w-[420px]">
          <Card className="bg-card/95 backdrop-blur-md border-border shadow-2xl rounded-none border-t-4 border-t-primary">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="size-5 text-primary" />
                <span className="text-xs font-mono font-semibold text-muted-foreground uppercase tracking-widest">Main Service Hub</span>
              </div>
              <CardTitle className="text-2xl font-bold font-default text-foreground uppercase tracking-tight">
                Dott's Garage
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground font-default">
                Albany's trusted automotive experts for over a century. Quality you can depend on.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-muted rounded-none border border-border shrink-0">
                  <Map className="size-5 text-foreground" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1 font-default">Physical Address</p>
                  <p className="text-sm text-muted-foreground font-mono">1177 Central Ave<br />Albany, NY 12205</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-2 bg-muted rounded-none border border-border shrink-0">
                  <Phone className="size-5 text-foreground" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1 font-default">Direct Line</p>
                  <p className="text-sm text-muted-foreground font-mono">(518) 459-3404<br /></p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="pt-2 pb-6">
              <Link to="https://maps.google.com" newTab className="w-full">
                <Button className="w-full rounded-none font-mono uppercase tracking-wider group bg-primary hover:bg-primary/90 text-primary-foreground" size="lg">
                  <Navigation className="size-4 mr-2 group-hover:animate-bounce" />
                  Get Directions
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>

      {}
      <div className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="size-5 text-primary" />
              <span className="text-sm font-mono font-semibold text-primary uppercase tracking-wider">Local Presence</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 uppercase tracking-tight">
              Serving the Capital Region
            </h2>
            <p className="text-lg text-muted-foreground font-default">
              While our main shop in Albany remains our heart, Dott's Garage operates specialized service points to ensure every driver in the region has access to heritage-grade mechanical care.
            </p>
          </div>
          <Link to="/BookAppointmentPage">
            <Button variant="outline" className="rounded-none font-mono uppercase tracking-wider border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              Book at a Hub <ArrowUpRight className="size-4 ml-2" />
            </Button>
          </Link>
        </div>

        <div ref={tableRef} className={`transition-all duration-1000 ease-out border border-border bg-card shadow-lg ${isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="w-[300px] font-mono text-xs uppercase tracking-wider text-foreground">Location Name</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider text-foreground">City</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider text-foreground hidden md:table-cell">Specialization</TableHead>
                <TableHead className="text-right font-mono text-xs uppercase tracking-wider text-foreground">Availability</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hubs.map((hub, i) => <TableRow key={hub.id} data-index={i} className="group border-b border-border/50">
                  <TableCell className="font-bold text-foreground font-default uppercase tracking-tight">
                    {hub.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono text-sm">
                    {hub.location}
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden md:table-cell font-default">
                    {hub.focus}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-mono font-medium uppercase tracking-wider border ${hub.status === 'Active' ? 'bg-primary/10 text-primary border-primary/20' : 'bg-chart-3/10 text-chart-3 border-chart-3/20'}`}>
                      {hub.status}
                    </span>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>
      </div>
    </section>;
}